import type { ReactElement } from 'react';
import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Calendar,
  MapPin,
  Store,
  User,
  Building2,
  RefreshCw,
  X,
  RotateCcw,
  FileText,
} from 'lucide-react';
import { Wizard } from './Wizard';
import FilterInput from '../../components/shared/FilterInput';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import TileButton from '../../components/shared/TileButton';
import { useToast } from '../../hooks/useToast';
import type { ReproveConfig, ReprovePayload } from '../../types/reprove';
import { loadDynamicConfig } from '../../utils/configAdapter';
import { saveReport } from '../../services/reportService';
import { reproveDataService } from '../../services/reproveDataService';
import type {
  ReproveRepresentative,
  ReproveCity,
  ReproveChain,
  ReproveStore,
  ReproveSeller,
  ReproveStoresResponse,
} from '../../services/reproveDataService';

interface ReproveMainProps {
  onBack: () => void;
  onNavigateToReports: () => void;
}

type FilterState = {
  representante: string;
  ciudad: string;
  cadena: string;
  puntoVenta: string;
  cuotaPiezas: number | '';
  date: string;
  vendedora: boolean;
  nombreVendedora: string;
  ventaPromedio: number | '';
};

type SaveState = 'idle' | 'saving' | 'success' | 'error';

export default function ReproveMain({
  onBack,
  onNavigateToReports,
}: ReproveMainProps): ReactElement {
  const { addToast } = useToast();

  const [selected, setSelected] = useState<FilterState>({
    representante: '',
    ciudad: '',
    cadena: '',
    puntoVenta: '',
    cuotaPiezas: '',
    date: '',
    vendedora: false,
    nombreVendedora: '',
    ventaPromedio: '',
  });

  const [masterData, setMasterData] = useState<{
    representatives: ReproveRepresentative[];
    cities: ReproveCity[];
    chains: ReproveChain[];
    pointsOfSale: ReproveStore[];
    availableSellers: ReproveSeller[];
  }>({
    representatives: [],
    cities: [],
    chains: [],
    pointsOfSale: [],
    availableSellers: [],
  });

  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [masterDataLoading, setMasterDataLoading] = useState(true);
  const [masterDataError, setMasterDataError] = useState<string | null>(null);

  const [config, setConfig] = useState<ReproveConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configSource, setConfigSource] = useState<'dynamic' | 'fallback'>(
    'dynamic'
  );

  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [lastSavedId, setLastSavedId] = useState<string | null>(null);

  const isAsideBlocked = creating && !surveyCompleted && saveState !== 'saving';

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileView, setMobileView] = useState<'filters' | 'survey'>('filters');

  useEffect(() => {
    const loadRepresentatives = async () => {
      setMasterDataLoading(true);
      setMasterDataError(null);

      try {
        const representatives = await reproveDataService.getRepresentatives();
        setMasterData((prev) => ({ ...prev, representatives }));
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : 'Error desconocido';
        setMasterDataError(errorMsg);
        addToast('error', `Error al cargar representantes: ${errorMsg}`, 8000);
      } finally {
        setMasterDataLoading(false);
      }
    };

    loadRepresentatives();
  }, [addToast]);

  useEffect(() => {
    if (!selected.representante) {
      setMasterData((prev) => ({ ...prev, cities: [] }));
      return;
    }

    const loadCities = async () => {
      try {
        const cities = await reproveDataService.getCitiesForRepresentative(
          selected.representante
        );
        setMasterData((prev) => ({ ...prev, cities }));
      } catch (error) {
        console.error('Error loading cities:', error);
        setMasterData((prev) => ({ ...prev, cities: [] }));
      }
    };

    loadCities();
  }, [selected.representante]);

  const ciudadesForRep = useMemo(() => {
    return masterData.cities.map((city) => ({
      id: city.id,
      name: city.name,
    }));
  }, [masterData.cities]);

  useEffect(() => {
    const loadConfig = async () => {
      setConfigLoading(true);
      setConfigError(null);

      try {
        const result = await loadDynamicConfig();
        setConfig(result.config);
        setConfigSource(result.source);

        if (result.errors && result.errors.length > 0) {
          setConfigError(result.errors.join(', '));
        }
      } catch (error) {
        setConfigError('Error crítico cargando configuración');
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, []);

  const representativeOptions = useMemo(
    () =>
      masterData.representatives.map((rep) => ({
        id: rep.id,
        name: rep.name,
      })),
    [masterData.representatives]
  );

  useEffect(() => {
    if (!selected.representante || !selected.ciudad) {
      setMasterData((prev) => ({ ...prev, chains: [] }));
      return;
    }

    const loadChains = async () => {
      try {
        const chains =
          await reproveDataService.getChainsForRepresentativeInCity(
            selected.representante,
            selected.ciudad
          );
        setMasterData((prev) => ({ ...prev, chains }));
      } catch (error) {
        console.error('Error loading chains:', error);
        setMasterData((prev) => ({ ...prev, chains: [] }));
      }
    };

    loadChains();
  }, [selected.representante, selected.ciudad]);

  const cadenasForCity = useMemo(() => {
    return masterData.chains.map((chain) => ({
      id: chain.id,
      name: chain.name,
    }));
  }, [masterData.chains]);

  useEffect(() => {
    if (!selected.representante || !selected.ciudad || !selected.cadena) {
      setMasterData((prev) => ({
        ...prev,
        pointsOfSale: [],
        // availableSellers: [],
      }));
      return;
    }

    const loadStores = async () => {
      try {
        // Recibir objeto con stores y availableSellers
        const response: ReproveStoresResponse =
          await reproveDataService.getStoresForRepresentativeInCity(
            selected.representante,
            selected.ciudad,
            selected.cadena
          );

        // Log detallado de cada tienda
        response.stores.forEach((store, idx) => {
          const activeSellers = store.sellers?.filter((s) => s.active) || [];
        });

        // Guardar ambos arrays
        setMasterData((prev) => ({
          ...prev,
          pointsOfSale: response.stores,
          // availableSellers: response.availableSellers,
        }));
      } catch (error) {
        console.error('Error loading stores:', error);
        setMasterData((prev) => ({
          ...prev,
          pointsOfSale: [],
          availableSellers: [],
        }));
      }
    };

    loadStores();
  }, [selected.representante, selected.ciudad, selected.cadena]);

  const puntosForCityAndChain = useMemo(() => {
    return masterData.pointsOfSale.map((pos) => ({
      id: pos.id,
      name: pos.name,
    }));
  }, [masterData.pointsOfSale]);

  const vendedorasForRep = useMemo(() => {
    return masterData.availableSellers.map((seller) => ({
      id: seller.id,
      name: seller.name,
    }));
  }, [masterData.availableSellers]);

  // Auto-seleccionar "Sí" en vendedora si hay sellers disponibles en el punto de venta
  useEffect(() => {
    if (!selected.puntoVenta) {
      setSelected((s) => ({
        ...s,
        vendedora: false,
        nombreVendedora: '',
      }));
      return;
    }

    // Buscar el punto de venta seleccionado
    const selectedStore = masterData.pointsOfSale.find(
      (pos) => pos.id === selected.puntoVenta
    );

    if (!selectedStore) {
      setSelected((s) => ({
        ...s,
        vendedora: false,
        nombreVendedora: '',
      }));
      return;
    }

    // Auto-seleccionar "Sí" si hay sellers disponibles en la CIUDAD
    if (masterData.availableSellers.length > 0) {
      setSelected((s) => ({
        ...s,
        vendedora: true,
      }));
    } else {
      setSelected((s) => ({
        ...s,
        vendedora: false,
        nombreVendedora: '',
      }));
    }
  }, [
    selected.puntoVenta,
    masterData.pointsOfSale,
    masterData.availableSellers,
  ]);

  useEffect(() => {
    setSelected((s) => ({
      ...s,
      ciudad: '',
      cadena: '',
      puntoVenta: '',
      nombreVendedora: '',
    }));
  }, [selected.representante]);

  useEffect(() => {
    setSelected((s) => ({ ...s, cadena: '', puntoVenta: '' }));
  }, [selected.ciudad]);

  useEffect(() => {
    setSelected((s) => ({ ...s, puntoVenta: '' }));
  }, [selected.cadena]);

  useEffect(() => {
    if (!selected.representante || !selected.ciudad) {
      setMasterData((prev) => ({ ...prev, availableSellers: [] }));
      return;
    }

    const loadSellers = async () => {
      try {
        const sellers =
          await reproveDataService.getSellersForRepresentativeInCity(
            selected.representante,
            selected.ciudad
          );
        setMasterData((prev) => ({
          ...prev,
          availableSellers: sellers,
        }));
      } catch (error) {
        console.error('Error loading sellers:', error);
        setMasterData((prev) => ({ ...prev, availableSellers: [] }));
      }
    };

    loadSellers();
  }, [selected.representante, selected.ciudad]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const canCreate = useMemo(() => {
    const baseOk =
      !!selected.representante &&
      !!selected.ciudad &&
      !!selected.cadena &&
      !!selected.puntoVenta &&
      typeof selected.cuotaPiezas === 'number' &&
      selected.cuotaPiezas > 0 &&
      !!selected.date;

    if (!baseOk) return false;
    if (!selected.vendedora) return true;

    return (
      !!selected.nombreVendedora &&
      typeof selected.ventaPromedio === 'number' &&
      selected.ventaPromedio > 0
    );
  }, [selected]);

  const resetToInitialState = useCallback(() => {
    setSelected({
      representante: '',
      ciudad: '',
      cadena: '',
      puntoVenta: '',
      cuotaPiezas: '',
      date: '',
      vendedora: false,
      nombreVendedora: '',
      ventaPromedio: '',
    });
    setCreating(false);
    setSurveyCompleted(false);
    setSaveState('idle');
    setLastSavedId(null);
    if (isMobile) {
      setMobileView('filters');
    }
  }, [isMobile]);

  const handleCreate = (): void => {
    if (!canCreate || !config) return;
    setCreating(true);
    setSurveyCompleted(false);
    setSaveState('idle');
    setLastSavedId(null);
    if (isMobile) {
      setMobileView('survey');
    }
  };

  const handleCancelSurvey = (): void => {
    setShowCancelModal(true);
  };

  const confirmCancelSurvey = (): void => {
    resetToInitialState();
    setShowCancelModal(false);
    addToast('info', 'Encuesta cancelada', 3000);
  };

  const onSave = async (payload: ReprovePayload): Promise<void> => {
    setSaveState('saving');
    addToast('info', 'Guardando reporte...', 3000);

    try {
      const result = await saveReport(payload, selected);

      if (result.success && result.reportId) {
        setLastSavedId(result.reportId);
        setSaveState('success');
        setSurveyCompleted(true);
        setCreating(false);
        addToast('success', '✅ Reporte guardado exitosamente', 5000);

        if (isMobile) {
          setTimeout(() => {
            setMobileView('filters');
          }, 1000);
        }
      } else {
        setSaveState('error');
        addToast(
          'error',
          `Error: ${result.error ?? 'Error desconocido'}`,
          8000
        );
      }
    } catch (error) {
      setSaveState('error');
      addToast('error', 'Error inesperado al guardar el reporte', 8000);
    }
  };

  const handleRetryCreate = (): void => {
    setSaveState('idle');
    setCreating(true);
    setSurveyCompleted(false);
  };

  const handleRetryMasterData = async (): Promise<void> => {
    setMasterDataLoading(true);
    setMasterDataError(null);

    try {
      const representatives = await reproveDataService.getRepresentatives();
      setMasterData((prev) => ({ ...prev, representatives }));
      addToast('success', 'Datos cargados correctamente', 3000);
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'Error desconocido';
      setMasterDataError(errorMsg);
      addToast('error', `Error al cargar datos: ${errorMsg}`, 8000);
    } finally {
      setMasterDataLoading(false);
    }
  };

  const handleNavigateToReports = (): void => {
    onNavigateToReports();
  };

  const handleCreateAnother = (): void => {
    resetToInitialState();
    addToast('info', 'Listo para crear nuevo reporte', 3000);
  };

  const findName = (
    list: Array<{ id: string; name: string }>,
    id: string
  ): string => {
    return list.find((o) => o.id === id)?.name ?? '';
  };

  const repName = findName(representativeOptions, selected.representante);
  const cityName = findName(ciudadesForRep, selected.ciudad);
  const posName = findName(puntosForCityAndChain, selected.puntoVenta);

  if (configLoading || masterDataLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner
          message={
            configLoading && masterDataLoading
              ? 'Cargando configuración y datos...'
              : configLoading
              ? 'Cargando configuración...'
              : 'Cargando datos maestros...'
          }
        />
      </div>
    );
  }

  if (masterDataError) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center p-8 bg-red-50 rounded-lg border border-red-200 max-w-md">
          <div className="text-red-600 text-lg font-medium mb-2">
            ⚠️ Error cargando datos
          </div>
          <div className="text-red-700 text-sm mb-4">{masterDataError}</div>
          <div className="space-x-2">
            <TileButton
              label="Reintentar"
              Icon={RefreshCw}
              onClick={handleRetryMasterData}
              variant="filled"
              color="gray"
              size="sm"
            />
            <TileButton
              label="Continuar con datos limitados"
              onClick={() => {
                setMasterDataError(null);
              }}
              variant="outline"
              color="gray"
              size="sm"
            />
          </div>
        </div>
      </div>
    );
  }

  if (configError || !config) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center p-8 bg-red-50 rounded-lg border border-red-200 max-w-md">
          <div className="text-red-600 text-lg font-medium mb-2">
            ⚠️ Error de configuración
          </div>
          <div className="text-red-700 text-sm mb-4">
            {configError ?? 'No se pudo cargar la configuración'}
          </div>
          <TileButton
            label="Reintentar"
            onClick={() => {
              window.location.reload();
            }}
            variant="filled"
            color="gray"
            size="sm"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full relative">
      {(!isMobile || (isMobile && mobileView === 'filters')) && (
        <aside
          className={`
          ${
            isMobile
              ? 'fixed top-14 inset-x-0 bottom-0 z-20 bg-white'
              : 'w-80 border-r border-gray-200'
          }
          ${isAsideBlocked && !isMobile ? 'pointer-events-none relative' : ''}
          bg-white p-4 sm:p-6 overflow-y-auto
        `}
        >
          {isAsideBlocked && !isMobile && (
            <div className="absolute inset-0 bg-gray-50/80 z-10" />
          )}

          {isMobile && (
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                Crear Reporte
              </h2>
              <p className="text-sm text-gray-500">
                Completa los datos para iniciar la encuesta
              </p>
            </div>
          )}

          {configSource === 'fallback' && (
            <div className="mb-4 p-3 rounded-lg border-l-4 bg-orange-50 border-orange-400 text-orange-700">
              <div className="text-sm font-medium">
                ⚠️ Configuración por defecto
              </div>
              <div className="text-xs mt-1">
                No se pudo cargar desde la base de datos
              </div>
            </div>
          )}

          {saveState === 'success' && lastSavedId && (
            <div className="mb-4 p-4 rounded-lg border-l-4 bg-green-50 border-green-400 text-green-700">
              <div className="text-sm font-medium mb-3">
                ✅ Reporte guardado
              </div>
              <div className="text-xs mb-3">
                ID: {lastSavedId.substring(0, 8)}...
              </div>
              <div className="space-y-2">
                <TileButton
                  label="Ver reportes"
                  Icon={FileText}
                  onClick={handleNavigateToReports}
                  variant="filled"
                  color="primary"
                  size="sm"
                  width="full"
                />
                <TileButton
                  label="Crear otro reporte"
                  Icon={RotateCcw}
                  onClick={handleCreateAnother}
                  variant="outline"
                  color="gray"
                  size="sm"
                  width="full"
                />
              </div>
            </div>
          )}

          <div className="mb-4 p-3 rounded-lg border-l-4 bg-blue-50 border-blue-400 text-blue-700">
            <div className="text-sm font-medium">📊 Datos cargados:</div>
            <div className="text-xs mt-1">
              {masterData.representatives.length} reps,{' '}
              {masterData.cities.length} ciudades,{' '}
              {masterData.pointsOfSale.length} PDVs
            </div>
          </div>

          <FilterInput
            label="Representante"
            icon={User}
            type="select"
            value={selected.representante}
            onChange={(v) => {
              setSelected((s) => ({ ...s, representante: String(v) }));
            }}
            options={representativeOptions}
            placeholder="Seleccionar"
            className="mb-4"
            disabled={isAsideBlocked && !isMobile}
          />

          <FilterInput
            label="Ciudad"
            icon={MapPin}
            type="select"
            value={selected.ciudad}
            onChange={(v) => {
              setSelected((s) => ({ ...s, ciudad: String(v) }));
            }}
            options={ciudadesForRep}
            placeholder="Seleccionar"
            className="mb-4"
            disabled={!selected.representante || (isAsideBlocked && !isMobile)}
          />

          <FilterInput
            label="Cadena"
            icon={Building2}
            type="select"
            value={selected.cadena}
            onChange={(v) => {
              setSelected((s) => ({ ...s, cadena: String(v) }));
            }}
            options={cadenasForCity}
            placeholder="Seleccionar"
            className="mb-4"
            disabled={!selected.ciudad || (isAsideBlocked && !isMobile)}
          />

          <FilterInput
            label="Punto de venta"
            icon={Store}
            type="select"
            value={selected.puntoVenta}
            onChange={(v) => {
              setSelected((s) => ({ ...s, puntoVenta: String(v) }));
            }}
            options={puntosForCityAndChain}
            placeholder="Seleccionar"
            className="mb-4"
            disabled={!selected.cadena || (isAsideBlocked && !isMobile)}
          />

          <FilterInput
            label="Cuota de venta (piezas)"
            icon={Building2}
            type="number"
            value={selected.cuotaPiezas}
            onChange={(v) => {
              setSelected((s) => ({ ...s, cuotaPiezas: v as number | '' }));
            }}
            className="mb-4"
            disabled={isAsideBlocked && !isMobile}
          />

          <FilterInput
            label="Fecha"
            icon={Calendar}
            type="date"
            value={selected.date}
            onChange={(v) => {
              setSelected((s) => ({ ...s, date: String(v) }));
            }}
            className="mb-4"
            disabled={isAsideBlocked && !isMobile}
          />

          <FilterInput
            label="Vendedora"
            icon={User}
            type="radio"
            value={selected.vendedora}
            onChange={(v) => {
              setSelected((s) => ({ ...s, vendedora: Boolean(v) }));
            }}
            radioOptions={[
              { label: 'Sí', value: true },
              { label: 'No', value: false },
            ]}
            className="mb-4"
            disabled={isAsideBlocked && !isMobile}
          />

          {selected.vendedora && (
            <>
              <FilterInput
                label="Nombre vendedora"
                icon={User}
                type="select"
                value={selected.nombreVendedora}
                onChange={(v) => {
                  setSelected((s) => ({ ...s, nombreVendedora: String(v) }));
                }}
                options={vendedorasForRep}
                placeholder="Seleccionar"
                className="mb-4"
                disabled={
                  !selected.representante || (isAsideBlocked && !isMobile)
                }
              />
              <FilterInput
                label="Venta promedio (piezas)"
                icon={Store}
                type="number"
                value={selected.ventaPromedio}
                onChange={(v) => {
                  setSelected((s) => ({
                    ...s,
                    ventaPromedio: v as number | '',
                  }));
                }}
                className="mb-4"
                disabled={isAsideBlocked && !isMobile}
              />
            </>
          )}

          {saveState !== 'success' && (
            <div
              className={
                isMobile
                  ? 'sticky bottom-0 bg-white pt-4 -mx-4 px-4 border-t'
                  : ''
              }
            >
              <TileButton
                label={creating ? 'Encuesta activa...' : 'Iniciar Encuesta'}
                onClick={handleCreate}
                disabled={
                  !canCreate || (isAsideBlocked && !isMobile) || !config
                }
                variant="filled"
                color={
                  canCreate && (!isAsideBlocked || isMobile) && config
                    ? 'primary'
                    : 'gray'
                }
                size={isMobile ? 'lg' : 'md'}
                width="full"
                className={isMobile ? 'h-14' : ''}
              />
            </div>
          )}
        </aside>
      )}

      {(!isMobile || (isMobile && mobileView === 'survey')) && (
        <main
          className={`flex-1 ${
            isMobile ? 'fixed top-14 inset-x-0 bottom-0 z-30 bg-white' : 'p-6'
          }`}
        >
          {isMobile && creating && mobileView === 'survey' && (
            <button
              onClick={handleCancelSurvey}
              className="fixed top-20 right-4 z-50 p-3 bg-white border-2 border-gray-300 rounded-full shadow-lg hover:bg-gray-50 active:scale-95 transition-transform"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>
          )}

          {!isMobile && (
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-xl font-semibold text-gray-800">
                {creating ? 'Encuesta PDV' : 'Listo para crear reporte'}
              </h1>
              {creating && (
                <TileButton
                  label="Cancelar"
                  Icon={X}
                  onClick={handleCancelSurvey}
                  variant="outline"
                  color="gray"
                  size="sm"
                />
              )}
            </div>
          )}

          {!creating ? (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center p-4">
                <div className="text-4xl mb-3">📋</div>
                <div className="text-lg font-medium mb-2">
                  {saveState === 'success'
                    ? '✅ Reporte guardado exitosamente'
                    : 'Listo para crear reporte'}
                </div>
                <div className="text-sm text-gray-600">
                  {saveState === 'success'
                    ? 'Puedes crear otro reporte o ver los reportes existentes'
                    : 'Completa los filtros y presiona "Iniciar Encuesta"'}
                </div>
                <div className="mt-4 text-xs text-gray-400">
                  Configuración: {config.tabs.length} secciones | Datos:{' '}
                  {masterData.representatives.length} representantes
                  {configSource === 'fallback' && ' (modo fallback)'}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full">
              <Wizard
                config={config}
                onSave={onSave}
                onSurveyComplete={() => {
                  setSurveyCompleted(true);
                }}
                headerMeta={{
                  representante: repName,
                  ciudad: cityName,
                  puntoVenta: posName,
                  date: selected.date,
                }}
                isMobile={isMobile}
              />
            </div>
          )}
        </main>
      )}

      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              ¿Cancelar encuesta?
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              {creating && (surveyCompleted || saveState !== 'idle')
                ? 'Se perderá todo el progreso del reporte. Esta acción no se puede deshacer.'
                : '¿Estás seguro de que quieres cancelar la encuesta?'}
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
              <TileButton
                label="No, continuar"
                onClick={() => {
                  setShowCancelModal(false);
                }}
                variant="outline"
                color="gray"
                size="md"
                width="full"
              />
              <TileButton
                label="Sí, cancelar"
                onClick={confirmCancelSurvey}
                variant="filled"
                color="gray"
                size="md"
                width="full"
                className="bg-red-600 hover:bg-red-700 border-red-600"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
